import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  Description as DocumentIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const DocumentationSection = ({ bol, onUpdate, canEdit = true }) => {
  const theme = useTheme();

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <DocumentIcon />
        Documentation
      </SectionTitle>

      <Grid container spacing={3}>
        {/* Payment Terms */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
            Shipping Payment Terms
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Freight Terms</InputLabel>
            <Select
              value={bol.freight_terms || 'Prepaid'}
              onChange={(e) => canEdit && onUpdate({ freight_terms: e.target.value })}
              label="Freight Terms"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              <MenuItem value="Prepaid">Prepaid</MenuItem>
              <MenuItem value="Collect">Collect</MenuItem>
              <MenuItem value="Third Party">Third Party</MenuItem>
              <MenuItem value="FOB Origin">FOB Origin</MenuItem>
              <MenuItem value="FOB Destination">FOB Destination</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Payment Responsibility</InputLabel>
            <Select
              value={bol.payment_responsibility || 'Consignee'}
              onChange={(e) => canEdit && onUpdate({ payment_responsibility: e.target.value })}
              label="Payment Responsibility"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              <MenuItem value="Shipper">Shipper</MenuItem>
              <MenuItem value="Consignee">Consignee</MenuItem>
              <MenuItem value="Third Party">Third Party</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Freight Charges"
            type="number"
            fullWidth
            value={bol.freight_charges || ''}
            onChange={(e) => canEdit && onUpdate({ freight_charges: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="COD Amount (if applicable)"
            type="number"
            fullWidth
            value={bol.cod_amount || ''}
            onChange={(e) => canEdit && onUpdate({ cod_amount: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        {/* Insurance Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 500, color: 'text.secondary' }}>
            Insurance Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bol.insurance_required || false}
                onChange={(e) => canEdit && onUpdate({ insurance_required: e.target.checked })}
                sx={{
                  color: theme.palette.primary.main,
                  opacity: canEdit ? 1 : 0.5,
                  pointerEvents: canEdit ? 'auto' : 'none',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Insurance Required
              </Typography>
            }
          />
        </Grid>

        {bol.insurance_required && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Insurance Provider"
                fullWidth
                value={bol.insurance_provider || ''}
                onChange={(e) => canEdit && onUpdate({ insurance_provider: e.target.value })}
                placeholder="Company name"
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Policy Number"
                fullWidth
                value={bol.insurance_policy_number || ''}
                onChange={(e) => canEdit && onUpdate({ insurance_policy_number: e.target.value })}
                placeholder="Policy #"
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Coverage Amount"
                type="number"
                fullWidth
                value={bol.insurance_coverage || ''}
                onChange={(e) => canEdit && onUpdate({ insurance_coverage: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Phone"
                fullWidth
                value={bol.insurance_contact || ''}
                onChange={(e) => canEdit && onUpdate({ insurance_contact: e.target.value })}
                placeholder="Insurance contact"
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Coverage Details"
                fullWidth
                multiline
                rows={2}
                value={bol.insurance_details || ''}
                onChange={(e) => canEdit && onUpdate({ insurance_details: e.target.value })}
                placeholder="Additional insurance information..."
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
          </>
        )}

        {/* Additional Documentation */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 500, color: 'text.secondary' }}>
            Additional Documentation
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Additional Notes"
            fullWidth
            multiline
            rows={3}
            value={bol.documentation_notes || ''}
            onChange={(e) => canEdit && onUpdate({ documentation_notes: e.target.value })}
            placeholder="Any additional documentation or reference numbers..."
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Reference Number 1"
            fullWidth
            value={bol.reference_1 || ''}
            onChange={(e) => canEdit && onUpdate({ reference_1: e.target.value })}
            placeholder="Optional reference"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Reference Number 2"
            fullWidth
            value={bol.reference_2 || ''}
            onChange={(e) => canEdit && onUpdate({ reference_2: e.target.value })}
            placeholder="Optional reference"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default DocumentationSection;